import { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge
} from '../components/ui';
import { 
  User, Mail, Calendar, Crown, Zap, CreditCard, 
  ExternalLink, Loader2, CheckCircle, AlertCircle, Settings as SettingsIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function Settings() {
  const { user, profile, refreshProfile, tier, isAdmin, isPro, isTrialActive, isTrialExpired, daysRemaining } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
  });

  // Initialize form data
  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        email: profile.email || user?.email || '',
      });
    }
  }, [profile, user]);

  // Handle successful payment redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true' && user) {
      verifyPayment(sessionId);
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, user]);

  // Reset loading on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLoading(false);
        setVerifying(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const verifyPayment = async (sessionId) => {
    setVerifying(true);
    const tid = toast.loading('Verifying your subscription...');

    try {
      const res = await authFetch('/api/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify_checkout',
          sessionId,
          userId: user.id,
        }),
      });

      const data = await res.json();

      if (data.success && data.paid) {
        toast.success(`Welcome to ${data.tier === 'pro' ? 'Pro' : 'Starter'}! 🎉`, { id: tid });
        // Refresh profile to get updated tier
        await refreshProfile();
      } else if (data.paid === false) {
        toast.error('Payment not completed. Please try again.', { id: tid });
      } else {
        toast.error(data.error || 'Failed to verify subscription', { id: tid });
      }
    } catch (e) {
      toast.error('Failed to verify subscription', { id: tid });
      console.error('Verify error:', e);
    }

    setVerifying(false);
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    const tid = toast.loading('Opening billing portal...');

    try {
      const res = await authFetch('/api/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'portal',
          userId: user.id,
        }),
      });

      const data = await res.json();

      if (data.success && data.portalUrl) {
        toast.dismiss(tid);
        window.location.href = data.portalUrl;
      } else {
        toast.error(data.error || 'Failed to open billing portal', { id: tid });
        setLoading(false);
      }
    } catch (e) {
      toast.error('Failed to open billing portal', { id: tid });
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!formData.username.trim()) {
      toast.error('Username is required');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          username: formData.username.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile saved');
      await refreshProfile();
    } catch (e) {
      toast.error(e.message || 'Failed to save profile');
    }
    setSaving(false);
  };

  const getTierBadge = () => {
    if (isAdmin) {
      return <Badge className="bg-purple-600">Admin</Badge>;
    }
    if (isPro) {
      return <Badge className="bg-yellow-600">Pro</Badge>;
    }
    if (tier === 'starter') {
      return <Badge className="bg-blue-600">Starter</Badge>;
    }
    if (isTrialActive) {
      return <Badge variant="outline">Trial ({daysRemaining} days left)</Badge>;
    }
    if (isTrialExpired) {
      return <Badge variant="destructive">Trial Expired</Badge>;
    }
    return <Badge variant="outline">Free</Badge>;
  };

  const getSubscriptionStatus = () => {
    const status = profile?.subscription_status;
    if (status === 'active') {
      return <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Active</span>;
    }
    if (status === 'past_due') {
      return <span className="text-orange-600 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Past Due</span>;
    }
    if (status === 'canceled') {
      return <span className="text-red-600">Canceled</span>;
    }
    return <span className="text-muted-foreground">No subscription</span>;
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account and subscription</p>
      </div>

      {/* Verifying Payment Banner */}
      {verifying && (
        <div className="p-4 rounded-lg bg-blue-100 text-blue-800 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Verifying your subscription...
        </div>
      )}

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Username</label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Your username"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                value={formData.email}
                disabled
                className="mt-1 bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>
          </div>
          
          <Button onClick={handleSaveProfile} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <div className="flex items-center gap-2 mt-1">
                {tier === 'pro' ? (
                  <Crown className="h-5 w-5 text-yellow-500" />
                ) : tier === 'starter' ? (
                  <Zap className="h-5 w-5 text-blue-500" />
                ) : null}
                <span className="text-lg font-semibold capitalize">{tier}</span>
                {getTierBadge()}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <div className="mt-1">
                {getSubscriptionStatus()}
              </div>
            </div>
          </div>

          {profile?.subscription_period_end && (
            <div>
              <p className="text-sm text-muted-foreground">
                {profile.subscription_cancel_at_period_end ? 'Access until' : 'Next billing date'}
              </p>
              <p className="font-medium">
                {new Date(profile.subscription_period_end).toLocaleDateString()}
                {profile.subscription_cancel_at_period_end && (
                  <span className="text-orange-600 ml-2">(Cancels at period end)</span>
                )}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            {!isAdmin && (tier === 'trial' || isTrialExpired) && (
              <Button onClick={() => navigate('/subscribe')}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade Now
              </Button>
            )}
            
            {!isAdmin && tier === 'starter' && (
              <Button onClick={() => navigate('/subscribe')}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}

            {profile?.stripe_customer_id && (
              <Button variant="outline" onClick={handleManageSubscription} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-2" />
                )}
                Manage Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Account Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            Account Info
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* User ID intentionally hidden — surfacing it lets it be passed
              around in support tickets, screenshots, etc., which historically
              widened the blast radius of API-level data leaks. Support staff
              can look users up by email instead. */}
          <div className="grid gap-4 sm:grid-cols-2 text-sm">
            <div>
              <p className="text-muted-foreground">Member Since</p>
              <p className="mt-1">
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString()
                  : 'Unknown'}
              </p>
            </div>
            {profile?.trial_ends_at && tier === 'trial' && (
              <div>
                <p className="text-muted-foreground">Trial Ends</p>
                <p className="mt-1">
                  {new Date(profile.trial_ends_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
